import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faArrowUpRightFromSquare, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useGoogleLogin } from '@react-oauth/google';
import api from '../utils/api';
import { useCurrency } from '../context/CurrencyContext';
import { useWishlist } from '../context/WishlistContext';
import { collectDisplaySizes, isSizeInStock } from '../utils/sortSizes';
import { openExternalLink } from '../utils/openExternalLink';
import { getSessionId, getDevice, trackProductView, trackInterest, trackDealFeedEvent, getAnalyticsUserId, flushDealFeedEventsNow, trackMetaViewContent, trackMetaCompleteRegistration } from '../utils/analytics';
import {
  registerShownProducts,
  completeHeroGate,
  resetFeedRegistry,
} from '../utils/feedRegistry';
import {
  loadDealFeedSeen,
  rememberDealFeedSeen,
  createFeedSeed,
  dealFeedExcludeIds,
} from '../utils/dealFeedMemory';
import { formatGoogleAuthError, googleOriginHint } from '../utils/googleAuthErrors';
import '../StylesComponents/product-card.css';
import '../StylesComponents/deal-scroller.css';

const FEED_SECTION = 'deal_scroller';
const PAGE_SIZE = 12;
const PREFETCH_WITHIN = 3;
const PREVIEW_LANE_SIZE = 3;
const PREVIEW_BOOT_LANES = 4;
const SKIP_DWELL_MS = 1200;
const LONG_DWELL_MS = 3000;
const EMPTY_PAGE_LIMIT = 3;
/** At least every Nth rail slot (1-based: 3, 6, 9…) must be a partnered product. */
const PARTNERED_CADENCE = 3;

function productKey(product) {
  return String(product?.groupKey || product?._id || product?.id || '').trim();
}

function isPartneredProduct(product) {
  return Boolean(product?.isPartnered);
}

/**
 * Build the next rail segment so positions 3, 6, 9… are partnered when possible,
 * while other slots prefer organic (non-partnered) personalized picks.
 */
function weavePartneredCadence(railLength, organicIn, partneredIn) {
  const organic = [...(organicIn || [])];
  const partnered = [...(partneredIn || [])];
  const woven = [];

  const take = (list, preferPartnered) => {
    if (list.length === 0) return null;
    if (preferPartnered) {
      const idx = list.findIndex(isPartneredProduct);
      if (idx >= 0) return list.splice(idx, 1)[0];
      return null;
    }
    const idx = list.findIndex((p) => !isPartneredProduct(p));
    if (idx >= 0) return list.splice(idx, 1)[0];
    return list.shift();
  };

  while (organic.length > 0 || partnered.length > 0) {
    const absIndex = railLength + woven.length;
    const needsPartnered = (absIndex + 1) % PARTNERED_CADENCE === 0;

    if (needsPartnered) {
      const pick = take(partnered, true)
        || take(organic, true)
        || organic.shift()
        || partnered.shift();
      if (!pick) break;
      woven.push(pick);
    } else {
      const pick = take(organic, false)
        || organic.shift()
        || partnered.shift();
      if (!pick) break;
      woven.push(pick);
    }
  }

  return { woven, leftoverOrganic: organic, leftoverPartnered: partnered };
}
export const DEAL_SCROLLER_EXIT_KEY = 'stockroom_deal_scroller_exited';
export const DEAL_SCROLLER_AUDIENCE_KEY = 'stockroom_deal_audience';

// Gated ambient rail: alternate mens/womens while cycling categories.
const PREVIEW_LANES = [
  { gender: 'men', categories: 'Jackets,Hoodies,Sweaters,T-Shirts' },
  { gender: 'women', categories: 'Dresses,Jackets,Skirts,Sweaters' },
  { gender: 'men', categories: 'Shoes' },
  { gender: 'women', categories: 'Shoes' },
  { gender: 'men', categories: 'Accessories' },
  { gender: 'women', categories: 'Accessories' },
  { gender: 'men', categories: 'Activewear,Jeans,Pants,Shorts' },
  { gender: 'women', categories: 'Activewear,Jeans,Pants,Swimwear' },
];

function shuffleList(list) {
  const next = [...list];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
}

function interleaveBatches(batches) {
  const result = [];
  const max = Math.max(0, ...batches.map((batch) => batch.length));
  for (let i = 0; i < max; i += 1) {
    batches.forEach((batch) => {
      if (batch[i]) result.push(batch[i]);
    });
  }
  return result;
}

function productImage(product) {
  return product?.image
    || (Array.isArray(product?.images) && product.images[0])
    || '/images/default-product.jpg';
}

function discountFor(product) {
  const price = Number(product?.price) || 0;
  const original = Number(product?.originalPrice) || 0;
  if (product?.discountPercentage > 0) return Math.round(product.discountPercentage);
  if (original > price && original > 0) {
    return Math.round(((original - price) / original) * 100);
  }
  return 0;
}

function cleanName(name) {
  return String(name || '')
    .replace(/\b[A-Z0-9]{10,}\b/g, '')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
}

function stripHtml(value) {
  return String(value || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

function readStoredAudience() {
  try {
    const value = sessionStorage.getItem(DEAL_SCROLLER_AUDIENCE_KEY);
    if (value === 'men' || value === 'women') return value;
  } catch (_) {}
  return null;
}

function writeStoredAudience(audience) {
  try {
    sessionStorage.setItem(DEAL_SCROLLER_AUDIENCE_KEY, audience);
  } catch (_) {}
}

function audienceFromUser(user) {
  const profileGender = String(user?.styleProfile?.gender || '').toLowerCase();
  if (profileGender === 'mens' || profileGender === 'men') return 'men';
  if (profileGender === 'womens' || profileGender === 'women') return 'women';
  return null;
}

function toStyleProfileGender(audience) {
  if (audience === 'men') return 'mens';
  if (audience === 'women') return 'womens';
  return 'unspecified';
}

function formatHeartCount(count) {
  const n = Number(count) || 0;
  if (n < 1000) return String(n);
  if (n < 10000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return `${Math.round(n / 1000)}k`;
}

function readInterests() {
  try {
    return localStorage.getItem('stockroom_interests') || null;
  } catch {
    return null;
  }
}

function DealSlide({ product, position, isActive, priority = false, showHint, onEngage }) {
  const { convertPrice, getCurrencySymbol } = useCurrency();
  const {
    wishlistItems,
    toggleWishlist,
    removeFromWishlist,
    isInWishlist,
    loading,
  } = useWishlist();
  const impressedRef = useRef(false);
  const slideRef = useRef(null);
  const [captionOpen, setCaptionOpen] = useState(false);
  const [description, setDescription] = useState(() => stripHtml(product?.description));
  const [descriptionLoading, setDescriptionLoading] = useState(false);
  const [showSizeSelector, setShowSizeSelector] = useState(false);
  const [isHeartAnimating, setIsHeartAnimating] = useState(false);
  const [heartCount, setHeartCount] = useState(null);

  const productId = product._id || product.id;
  const isWishlisted = isInWishlist(productId);
  const discount = discountFor(product);
  const price = Number(product.price) || 0;
  const original = Number(product.originalPrice) || 0;

  const markEngage = (kind) => {
    onEngage?.(productId, kind);
  };

  useEffect(() => {
    setDescription(stripHtml(product?.description));
    setHeartCount(null);
    setCaptionOpen(false);
  }, [product?.description, productId]);

  useEffect(() => {
    if (!isActive) {
      setCaptionOpen(false);
      setShowSizeSelector(false);
    }
  }, [isActive]);

  // Prefetch full description after a short dwell — cancelled on fast swipes.
  useEffect(() => {
    if (!isActive || !productId || description) return undefined;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setDescriptionLoading(true);
      api.get(`/api/products/${productId}`)
        .then((res) => {
          if (cancelled) return;
          const full = res?.data?.mainProduct || res?.data?.product || res?.data;
          setDescription(stripHtml(full?.description));
        })
        .catch(() => {})
        .finally(() => {
          if (!cancelled) setDescriptionLoading(false);
        });
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [isActive, productId, description]);

  // Heart count after a short dwell so fast scrolls don't stampede the API.
  useEffect(() => {
    if (!isActive || !productId || heartCount !== null) return undefined;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      api.get(`/api/wishlist/heart-count/${encodeURIComponent(productId)}`)
        .then((res) => {
          if (cancelled) return;
          setHeartCount(Number(res?.data?.count) || 0);
        })
        .catch(() => {
          if (!cancelled) setHeartCount(0);
        });
    }, 450);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [isActive, productId, heartCount]);

  useEffect(() => {
    if (!slideRef.current || impressedRef.current) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !impressedRef.current) {
          impressedRef.current = true;
          observer.disconnect();
          api.post('/api/products/impression', {
            impressions: [{
              productId,
              feedSection: FEED_SECTION,
              position,
              sessionId: getSessionId(),
              device: getDevice(),
              userId: getAnalyticsUserId(),
            }],
          }).catch(() => {});
        }
      },
      { threshold: 0.55 }
    );
    observer.observe(slideRef.current);
    return () => observer.disconnect();
  }, [position, productId]);

  const formatPrice = (value) => `${getCurrencySymbol()}${convertPrice(value)}`;

  const handleCardTap = () => {
    markEngage('tap');
    trackInterest(product, 3);
    trackProductView({
      productId,
      eventType: 'card_tap',
      feedSection: FEED_SECTION,
      position,
    });
    trackMetaViewContent(product);
  };

  const handleGetDeal = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    markEngage('click');
    trackInterest(product, 6);
    try {
      await api.post('/api/products/click', {
        productId,
        feedSection: FEED_SECTION,
        position,
        sessionId: getSessionId(),
        device: getDevice(),
        userId: getAnalyticsUserId(),
        referrer: document.referrer || 'direct',
      });
    } catch (_) {}
    if (product.affiliateUrl) openExternalLink(product.affiliateUrl);
  };

  const pulseHeart = () => {
    setIsHeartAnimating(true);
    window.setTimeout(() => setIsHeartAnimating(false), 300);
  };

  const handleWishlistClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;

    if (isWishlisted) {
      const pId = String(productId || '').toLowerCase();
      const existingItem = wishlistItems.find((item) => (
        String(item.productId || '').toLowerCase() === pId
        || String(item._id || '').toLowerCase() === pId
      ));
      if (existingItem) {
        await removeFromWishlist(existingItem._id);
      } else {
        await toggleWishlist(product, 'M');
      }
      setHeartCount((prev) => Math.max(0, (Number(prev) || 0) - 1));
      trackDealFeedEvent({
        productId,
        eventType: 'wishlist_remove',
        feedSection: FEED_SECTION,
        position,
      });
      trackInterest(product, -2);
      return;
    }

    if (product.isPartnered) {
      pulseHeart();
      await toggleWishlist(product, 'ONESIZE');
      setHeartCount((prev) => (Number(prev) || 0) + 1);
      markEngage('wishlist');
      trackInterest(product, 5);
      trackDealFeedEvent({
        productId,
        eventType: 'wishlist_add',
        feedSection: FEED_SECTION,
        position,
      });
      return;
    }

    setShowSizeSelector((open) => !open);
  };

  const confirmSizeSelection = async (size) => {
    pulseHeart();
    setShowSizeSelector(false);
    await toggleWishlist(product, size);
    setHeartCount((prev) => (Number(prev) || 0) + 1);
    markEngage('wishlist');
    trackInterest(product, 5);
    trackDealFeedEvent({
      productId,
      eventType: 'wishlist_add',
      feedSection: FEED_SECTION,
      position,
    });
  };

  const closeSizeSelector = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setShowSizeSelector(false);
  };

  const toggleCaption = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!description && descriptionLoading) return;
    if (!description) return;
    setCaptionOpen((open) => {
      const next = !open;
      if (next) {
        markEngage('caption');
        trackInterest(product, 2);
        trackDealFeedEvent({
          productId,
          eventType: 'caption_open',
          feedSection: FEED_SECTION,
          position,
        });
      }
      return next;
    });
  };

  const displaySizes = collectDisplaySizes([product]);

  return (
    <article
      ref={slideRef}
      className={`deal-scroller-slide${isActive ? ' is-active' : ''}${captionOpen ? ' is-caption-open' : ''}`}
      data-position={position}
    >
      <div className="deal-scroller-media">
        <Link
          to={`/product/${productId}`}
          className="deal-scroller-media-link"
          onClick={handleCardTap}
          aria-label={`View ${product.name || 'product'}`}
        >
          <img
            src={productImage(product)}
            alt=""
            draggable={false}
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
          />
        </Link>
      </div>
      <div className="deal-scroller-scrim" aria-hidden="true" />

      {showSizeSelector && (
        <div
          className="deal-scroller-size-picker"
          role="dialog"
          aria-modal="true"
          aria-label="Select size"
          onClick={closeSizeSelector}
        >
          <div className="deal-scroller-size-picker-inner" onClick={(e) => e.stopPropagation()}>
            <div className="deal-scroller-size-picker-header">
              <h3 className="deal-scroller-size-picker-title">
                {displaySizes.length === 0 ? 'No sizes' : 'Select size'}
              </h3>
              <button
                type="button"
                className="deal-scroller-size-picker-close"
                onClick={closeSizeSelector}
                aria-label="Close size picker"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {displaySizes.length === 0 ? (
              <p className="deal-scroller-size-picker-empty">No verified retailer sizes</p>
            ) : (
              <div className="deal-scroller-size-picker-grid">
                {displaySizes.slice(0, 16).map((size) => {
                  const inStock = isSizeInStock(product, size);
                  return (
                    <button
                      key={size}
                      type="button"
                      className={`deal-scroller-size-picker-btn${!inStock ? ' is-sold-out' : ''}`}
                      disabled={!inStock}
                      title={!inStock ? 'Sold Out' : ''}
                      onClick={() => inStock && confirmSizeSelection(size)}
                    >
                      {String(size).toUpperCase()}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {showHint && (
        <div className="deal-scroller-hint" aria-hidden="true">
          <span>Swipe for next deal</span>
          <span className="deal-scroller-hint-chevron" />
        </div>
      )}

      <div className="deal-scroller-bottom">
        <div className="deal-scroller-meta">
          <Link
            to={`/all-brands?brand=${encodeURIComponent(product.brand || '')}`}
            className="deal-scroller-product-brand"
          >
            {String(product.brand || product.merchantName || 'Stockroom').toUpperCase()}
          </Link>
          <Link
            to={`/product/${productId}`}
            className="deal-scroller-product-name-link"
            onClick={handleCardTap}
          >
            <h2 className="deal-scroller-product-name">{cleanName(product.name)}</h2>
          </Link>
          <div className="deal-scroller-price-row">
            <span className="deal-scroller-price">{formatPrice(price)}</span>
            {original > price && (
              <span className="deal-scroller-msrp">{formatPrice(original)}</span>
            )}
            {discount > 0 && (
              <span className="deal-scroller-discount">{discount}% OFF</span>
            )}
          </div>

          {(description || descriptionLoading) && (
            <div className={`deal-scroller-caption${captionOpen ? ' is-open' : ''}`}>
              {!captionOpen ? (
                <button
                  type="button"
                  className="deal-scroller-caption-collapsed"
                  onClick={toggleCaption}
                  aria-expanded="false"
                  aria-label="Show product description"
                  disabled={!description}
                >
                  <span className="deal-scroller-caption-clip">
                    {description || (descriptionLoading ? 'Loading description' : '')}
                  </span>
                  <span className="deal-scroller-caption-more" aria-hidden="true">···</span>
                </button>
              ) : (
                <button
                  type="button"
                  className="deal-scroller-caption-expanded"
                  onClick={toggleCaption}
                  aria-expanded="true"
                  aria-label="Hide product description"
                >
                  <p className="deal-scroller-caption-text">{description}</p>
                </button>
              )}
            </div>
          )}
        </div>

        <div className="deal-scroller-side-actions">
          <button
            type="button"
            className={`overlay-action-btn deal-scroller-wishlist-btn${isWishlisted ? ' active' : ''}${isHeartAnimating ? ' animating' : ''}`}
            onClick={handleWishlistClick}
            disabled={loading}
            aria-label={isWishlisted ? 'Remove from watchlist' : 'Save to watchlist'}
            style={{ color: isWishlisted ? '#ff4444' : undefined }}
          >
            <FontAwesomeIcon icon={faHeart} />
            {heartCount > 0 && (
              <span className="deal-scroller-heart-count">{formatHeartCount(heartCount)}</span>
            )}
          </button>
          <button
            type="button"
            className="deal-scroller-side-btn deal-scroller-side-btn--deal"
            onClick={handleGetDeal}
            disabled={!product.affiliateUrl}
            aria-label="Get deal"
          >
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
          </button>
        </div>
      </div>
    </article>
  );
}

function AudienceGate({ currentUser, onSelectAudience, onLogin }) {
  const [authError, setAuthError] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setAuthLoading(true);
      setAuthError(null);
      try {
        const res = await api.post('/api/auth/google', {
          token: tokenResponse.access_token,
        });
        const data = res.data;
        localStorage.setItem('token', data.accessToken);
        localStorage.setItem('user', JSON.stringify(data.user));
        if (data.isNewUser) trackMetaCompleteRegistration();
        onLogin?.(data.user);
      } catch (err) {
        console.error('Deal scroller Google auth failed:', err);
        setAuthError(err.response?.data?.error || err.message || 'Google sign-in failed');
      } finally {
        setAuthLoading(false);
      }
    },
    onError: (error) => {
      setAuthLoading(false);
      const detail = formatGoogleAuthError(error);
      setAuthError(`${detail}. ${googleOriginHint().replace(/\n/g, ' ')}`);
    },
  });

  return (
    <div className="deal-scroller-gate" role="dialog" aria-modal="true" aria-label="Choose your feed">
      <div className="deal-scroller-gate-inner">
        <div className="deal-scroller-gate-heading">
          {!currentUser && (
            <p className="deal-scroller-gate-kicker">Stockroom Guest</p>
          )}
          <h2 className="deal-scroller-gate-title">Shop for</h2>
        </div>

        <div className="deal-scroller-audience-row">
          <button
            type="button"
            className="deal-scroller-audience-btn"
            onClick={() => onSelectAudience('men')}
          >
            Mens
          </button>
          <button
            type="button"
            className="deal-scroller-audience-btn"
            onClick={() => onSelectAudience('women')}
          >
            Womens
          </button>
        </div>

        {!currentUser && (
          <>
            <div className="deal-scroller-gate-divider" aria-hidden="true">
              <span>or</span>
            </div>
            <button
              className="deal-scroller-google-btn"
              type="button"
              disabled={authLoading}
              onClick={() => googleLogin()}
            >
              <svg className="deal-scroller-google-icon" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
              </svg>
              <span>{authLoading ? 'Connecting…' : 'Continue with Google'}</span>
            </button>
          </>
        )}

        {authError && (
          <p className="deal-scroller-gate-error">{authError}</p>
        )}
      </div>
    </div>
  );
}

function DealScroller({ currentUser, onLogin, onExit, isExiting = false }) {
  const initialAudience = audienceFromUser(currentUser) || readStoredAudience();
  const [audience, setAudience] = useState(initialAudience);
  const [gateOpen, setGateOpen] = useState(!initialAudience);
  const [products, setProducts] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hintVisible, setHintVisible] = useState(true);
  const railRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const seenIdsRef = useRef(new Set());
  const feedGenRef = useRef(0);
  const feedSeedRef = useRef(createFeedSeed());
  const pageRef = useRef(1);
  const partneredPageRef = useRef(1);
  const partneredQueueRef = useRef([]);
  const organicQueueRef = useRef([]);
  const previewLaneRef = useRef(0);
  const previewEmptyStreakRef = useRef(0);
  const previewLaneOrderRef = useRef(PREVIEW_LANES);
  const activeIndexRef = useRef(0);
  const dwellStartedAtRef = useRef(Date.now());
  const engagedRef = useRef(new Set());
  const productsRef = useRef([]);

  useEffect(() => {
    productsRef.current = products;
  }, [products]);

  const handleSlideEngage = useCallback((productId, _kind) => {
    if (productId) engagedRef.current.add(String(productId));
  }, []);

  const emitLeaveSignals = useCallback((leavingIndex, nextIndex) => {
    const list = productsRef.current;
    const leaving = list[leavingIndex];
    if (!leaving) return;

    const productId = leaving._id || leaving.id;
    if (!productId) return;

    const dwellMs = Math.max(0, Date.now() - (dwellStartedAtRef.current || Date.now()));
    trackDealFeedEvent({
      productId,
      eventType: 'dwell',
      feedSection: FEED_SECTION,
      position: leavingIndex,
      dwellMs,
    });

    if (dwellMs >= LONG_DWELL_MS) {
      trackInterest(leaving, 2);
    }

    const movedForward = typeof nextIndex === 'number' && nextIndex > leavingIndex;
    const wasEngaged = engagedRef.current.has(String(productId));
    if (movedForward && dwellMs < SKIP_DWELL_MS && !wasEngaged) {
      trackDealFeedEvent({
        productId,
        eventType: 'skip',
        feedSection: FEED_SECTION,
        position: leavingIndex,
        dwellMs,
      });
      trackInterest(leaving, -1);
    }
  }, []);

  const persistAudience = useCallback(async (nextAudience, user = currentUser) => {
    writeStoredAudience(nextAudience);
    if (!user) return;
    try {
      const res = await api.patch('/api/users/style-profile', {
        gender: toStyleProfileGender(nextAudience),
      });
      const nextUser = {
        ...user,
        styleProfile: {
          ...(user.styleProfile || {}),
          ...(res.data || { gender: toStyleProfileGender(nextAudience) }),
        },
      };
      localStorage.setItem('user', JSON.stringify(nextUser));
      onLogin?.(nextUser);
    } catch (err) {
      console.error('Failed to persist style profile gender:', err);
    }
  }, [currentUser, onLogin]);

  const fetchPreviewLane = useCallback(async (lane) => {
    const exclude = dealFeedExcludeIds(seenIdsRef.current);
    const res = await api.get('/api/products', {
      params: {
        page: 1,
        limit: PREVIEW_LANE_SIZE,
        sort: 'DiscountHighToLow',
        discountRanges: '10+',
        trending: 'true',
        feedContext: 'trending',
        gender: lane.gender,
        categories: lane.categories,
        ...(exclude.length > 0 ? { exclude: exclude.join(',') } : {}),
      },
    });
    return shuffleList(res?.data?.products || []);
  }, []);

  const fetchPersonalizedPage = useCallback(async ({ gender, partneredOnly = false } = {}) => {
    const activeGender = gender === undefined ? audience : gender;
    const interests = readInterests();
    const pageRefTarget = partneredOnly ? partneredPageRef : pageRef;
    const page = pageRefTarget.current;
    const exclude = dealFeedExcludeIds(seenIdsRef.current)
      .filter((id) => /^[a-f\d]{24}$/i.test(String(id)))
      .slice(-120);

    try {
      const res = await api.get('/api/products', {
        params: {
          page,
          limit: PAGE_SIZE,
          feedContext: FEED_SECTION,
          discountRanges: '50+',
          sort: 'Default',
          feedSeed: feedSeedRef.current,
          ...(partneredOnly ? { partnered: 'true' } : {}),
          ...(activeGender ? { gender: activeGender } : {}),
          ...(interests ? { interests } : {}),
          ...(exclude.length > 0 ? { exclude: exclude.join(',') } : {}),
        },
      });
      pageRefTarget.current = page + 1;
      return res?.data?.products || [];
    } catch (err) {
      // Keep the same page so a transient failure can retry.
      throw err;
    }
  }, [audience]);

  const takeUnseen = useCallback((batch) => {
    if (!Array.isArray(batch) || batch.length === 0) return [];
    const fresh = [];
    batch.forEach((product) => {
      const key = productKey(product);
      const offerId = String(product?._id || product?.id || '');
      if (!key) return;
      if (seenIdsRef.current.has(key)) return;
      if (offerId && seenIdsRef.current.has(offerId)) return;
      fresh.push(product);
      seenIdsRef.current.add(key);
      if (offerId) seenIdsRef.current.add(offerId);
    });
    if (fresh.length > 0) {
      registerShownProducts(fresh);
      rememberDealFeedSeen(
        fresh.flatMap((p) => {
          const key = productKey(p);
          const offerId = String(p._id || p.id || '');
          return [key, offerId].filter(Boolean);
        })
      );
    }
    return fresh;
  }, []);

  const refillPartneredQueue = useCallback(async (gender, minCount = 4) => {
    let guards = 0;
    while (partneredQueueRef.current.length < minCount && guards < EMPTY_PAGE_LIMIT) {
      guards += 1;
      // eslint-disable-next-line no-await-in-loop
      const raw = await fetchPersonalizedPage({ gender, partneredOnly: true });
      const fresh = takeUnseen(raw).filter(isPartneredProduct);
      if (fresh.length === 0) break;
      partneredQueueRef.current.push(...fresh);
    }
  }, [fetchPersonalizedPage, takeUnseen]);

  const drainQueuedSegment = useCallback(() => {
    if (organicQueueRef.current.length === 0 && partneredQueueRef.current.length === 0) {
      return [];
    }
    const { woven, leftoverOrganic, leftoverPartnered } = weavePartneredCadence(
      productsRef.current.length,
      organicQueueRef.current,
      partneredQueueRef.current,
    );
    organicQueueRef.current = leftoverOrganic;
    partneredQueueRef.current = leftoverPartnered;
    return woven;
  }, []);

  const buildPersonalizedSegment = useCallback(async (gender, organicRaw) => {
    const organicFresh = takeUnseen(organicRaw);
    const fromOrganicPartnered = [];
    const organicOnly = [];
    organicFresh.forEach((p) => {
      if (isPartneredProduct(p)) fromOrganicPartnered.push(p);
      else organicOnly.push(p);
    });
    partneredQueueRef.current.push(...fromOrganicPartnered);

    const slotsNeeded = Math.max(organicOnly.length + fromOrganicPartnered.length, 1);
    const partneredNeeded = Math.ceil((productsRef.current.length + slotsNeeded) / PARTNERED_CADENCE)
      - Math.floor(productsRef.current.length / PARTNERED_CADENCE);

    // Only hit the network if the warm partnered pool is short — never block first paint on extra pages.
    if (partneredQueueRef.current.length < Math.max(partneredNeeded, 2)) {
      await refillPartneredQueue(gender, Math.max(partneredNeeded, 2));
    }

    const organicPool = [...organicQueueRef.current, ...organicOnly];
    organicQueueRef.current = [];
    const { woven, leftoverOrganic, leftoverPartnered } = weavePartneredCadence(
      productsRef.current.length,
      organicPool,
      partneredQueueRef.current,
    );
    organicQueueRef.current = leftoverOrganic;
    partneredQueueRef.current = leftoverPartnered;
    return woven;
  }, [refillPartneredQueue, takeUnseen]);

  const startFeed = useCallback(async (nextAudience = null) => {
    const gen = ++feedGenRef.current;
    feedSeedRef.current = createFeedSeed();
    pageRef.current = 1;
    partneredPageRef.current = 1;
    partneredQueueRef.current = [];
    organicQueueRef.current = [];
    // Fresh uniqueness set for this scrolling session — never show a product twice here.
    seenIdsRef.current = new Set();
    resetFeedRegistry();
    engagedRef.current = new Set();
    dwellStartedAtRef.current = Date.now();
    activeIndexRef.current = 0;
    setProducts([]);
    productsRef.current = [];
    setHasMore(true);
    setActiveIndex(0);
    setHintVisible(true);
    setLoading(true);
    previewLaneRef.current = 0;
    previewEmptyStreakRef.current = 0;

    try {
      let items = [];

      if (!nextAudience) {
        const offset = Math.floor(Math.random() * PREVIEW_LANES.length);
        const laneOrder = [
          ...PREVIEW_LANES.slice(offset),
          ...PREVIEW_LANES.slice(0, offset),
        ];
        previewLaneOrderRef.current = laneOrder;
        const bootLanes = laneOrder.slice(0, PREVIEW_BOOT_LANES);
        const laneResults = await Promise.all(bootLanes.map((lane) => fetchPreviewLane(lane)));
        if (gen !== feedGenRef.current) return;
        const batches = laneResults.map((batch) => takeUnseen(batch));
        items = interleaveBatches(batches);
        previewLaneRef.current = PREVIEW_BOOT_LANES;
      } else {
        // Prefer products not already seen earlier in this browser tab when possible.
        const prior = loadDealFeedSeen();
        const excludeWarm = dealFeedExcludeIds(prior)
          .filter((id) => /^[a-f\d]{24}$/i.test(String(id)))
          .slice(-120);
        const interests = readInterests();
        const sharedParams = {
          page: 1,
          limit: PAGE_SIZE,
          feedContext: FEED_SECTION,
          discountRanges: '50+',
          sort: 'Default',
          feedSeed: feedSeedRef.current,
          gender: nextAudience,
          ...(interests ? { interests } : {}),
          ...(excludeWarm.length > 0 ? { exclude: excludeWarm.join(',') } : {}),
        };
        const [warm, warmPartnered] = await Promise.all([
          api.get('/api/products', { params: sharedParams }),
          api.get('/api/products', { params: { ...sharedParams, partnered: 'true' } }),
        ]);
        if (gen !== feedGenRef.current) return;
        pageRef.current = 2;
        partneredPageRef.current = 2;
        partneredQueueRef.current = takeUnseen(warmPartnered?.data?.products || []).filter(isPartneredProduct);
        items = await buildPersonalizedSegment(nextAudience, warm?.data?.products || []);
        if (gen !== feedGenRef.current) return;
        if (items.length < Math.min(6, PAGE_SIZE)) {
          const more = await fetchPersonalizedPage({ gender: nextAudience });
          if (gen !== feedGenRef.current) return;
          const extra = await buildPersonalizedSegment(nextAudience, more);
          items = [...items, ...extra];
        }
      }

      if (gen !== feedGenRef.current) return;
      productsRef.current = items;
      setProducts(items);
      setHasMore(items.length > 0);
    } catch (err) {
      console.error('Deal scroller fetch error:', err);
      if (gen === feedGenRef.current) {
        productsRef.current = [];
        setProducts([]);
      }
    } finally {
      completeHeroGate();
      if (gen === feedGenRef.current) setLoading(false);
    }
  }, [buildPersonalizedSegment, fetchPersonalizedPage, fetchPreviewLane, takeUnseen]);

  // Preview mix while gated; gendered feed once audience is chosen.
  useEffect(() => {
    startFeed(audience || null);
    // Only re-run when audience changes — not when fetch callbacks recreate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audience]);

  // If user signs in later with a stored style profile, adopt it when gate is open.
  useEffect(() => {
    if (!gateOpen || audience) return;
    const fromUser = audienceFromUser(currentUser);
    if (!fromUser) return;
    setAudience(fromUser);
    writeStoredAudience(fromUser);
    setGateOpen(false);
  }, [currentUser, gateOpen, audience]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMoreRef.current || products.length === 0) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      let fresh = [];
      let emptyStreak = 0;

      if (!audience) {
        const laneOrder = previewLaneOrderRef.current.length
          ? previewLaneOrderRef.current
          : PREVIEW_LANES;
        const lanes = [];
        for (let n = 0; n < 2; n += 1) {
          const lane = laneOrder[previewLaneRef.current % laneOrder.length];
          previewLaneRef.current += 1;
          lanes.push(lane);
        }
        const laneResults = await Promise.all(lanes.map((lane) => fetchPreviewLane(lane)));
        const batches = laneResults.map((batch) => takeUnseen(batch));
        fresh = interleaveBatches(batches);
        if (fresh.length === 0) {
          previewEmptyStreakRef.current += 1;
          if (previewEmptyStreakRef.current >= laneOrder.length) {
            setHasMore(false);
          }
        } else {
          previewEmptyStreakRef.current = 0;
        }
      } else {
        // Flush any leftover woven queue before another network round-trip.
        fresh = drainQueuedSegment();
        while (fresh.length === 0 && emptyStreak < EMPTY_PAGE_LIMIT) {
          emptyStreak += 1;
          // eslint-disable-next-line no-await-in-loop
          const raw = await fetchPersonalizedPage({ gender: audience });
          // eslint-disable-next-line no-await-in-loop
          fresh = await buildPersonalizedSegment(audience, raw);
        }
        if (fresh.length === 0) {
          setHasMore(false);
        }
      }

      if (fresh.length > 0) {
        const next = [...productsRef.current, ...fresh];
        productsRef.current = next;
        setProducts(next);
      }
    } catch (err) {
      console.error('Deal scroller load more error:', err);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [audience, buildPersonalizedSegment, drainQueuedSegment, fetchPersonalizedPage, fetchPreviewLane, hasMore, products.length, takeUnseen]);

  useEffect(() => {
    if (products.length === 0) return;
    if (activeIndex >= products.length - PREFETCH_WITHIN) {
      loadMore();
    }
  }, [activeIndex, products.length, loadMore]);

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return undefined;

    const onScroll = () => {
      const height = Math.max(rail.clientHeight, 1);
      const index = Math.round(rail.scrollTop / height);
      if (index !== activeIndexRef.current) {
        emitLeaveSignals(activeIndexRef.current, index);
        activeIndexRef.current = index;
        dwellStartedAtRef.current = Date.now();
        setActiveIndex(index);
      }
      if (index > 0 && !gateOpen) setHintVisible(false);

      if (rail.scrollTop + height * 2.5 >= rail.scrollHeight) {
        loadMore();
      }
    };

    rail.addEventListener('scroll', onScroll, { passive: true });
    return () => rail.removeEventListener('scroll', onScroll);
  }, [products.length, gateOpen, emitLeaveSignals, loadMore]);

  // Flush dwell/skip when leaving the scroller entirely.
  useEffect(() => () => {
    emitLeaveSignals(activeIndexRef.current, null);
    flushDealFeedEventsNow();
  }, [emitLeaveSignals]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onExit?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onExit]);

  // Ambient auto-scroll behind the glass gate — continuous catalogue loop.
  useEffect(() => {
    if (!gateOpen || products.length < 2) return undefined;
    const motionOk = window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
    if (!motionOk) return undefined;

    const timer = window.setInterval(() => {
      const rail = railRef.current;
      if (!rail) return;
      const height = Math.max(rail.clientHeight, 1);
      const current = Math.round(rail.scrollTop / height);
      const next = current + 1;

      if (next >= products.length) {
        if (hasMore) {
          loadMore();
          return;
        }
        // Loop back to the start for continuous ambient scroll
        rail.scrollTo({ top: 0, behavior: 'auto' });
        return;
      }

      rail.scrollTo({ top: next * height, behavior: 'smooth' });
    }, 3600);

    return () => window.clearInterval(timer);
  }, [gateOpen, products.length, hasMore, loadMore]);

  const handleSelectAudience = async (nextAudience) => {
    setAudience(nextAudience);
    setGateOpen(false);
    await persistAudience(nextAudience);
  };

  const handleGoogleLoginResult = (user) => {
    onLogin?.(user);
    const fromUser = audienceFromUser(user);
    if (fromUser) {
      setAudience(fromUser);
      writeStoredAudience(fromUser);
      setGateOpen(false);
    }
  };

  return (
    <div
      className={`deal-scroller${isExiting ? ' is-exiting' : ''}${gateOpen ? ' is-gated' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label="Deal Feed"
    >
      <button
        type="button"
        className="deal-scroller-backdrop"
        onClick={onExit}
        aria-label="Close Deal Feed"
        tabIndex={-1}
      />

      <div className="deal-scroller-stage">
        <div className="deal-scroller-top">
          <button type="button" className="deal-scroller-close" onClick={onExit} aria-label="View full site">
            <span className="deal-scroller-close-label">View Full Site</span>
          </button>
        </div>

        {loading && products.length === 0 ? (
          <div className="deal-scroller-loading">LOADING DEALS…</div>
        ) : products.length === 0 ? (
          <div className="deal-scroller-empty">
            No {audience === 'women' ? 'womens' : audience === 'men' ? 'mens' : ''} deals right now.
            <button type="button" className="deal-scroller-empty-exit" onClick={onExit}>
              Browse Stockroom
            </button>
          </div>
        ) : (
          <div
            className="deal-scroller-rail"
            ref={railRef}
            aria-hidden={gateOpen ? 'true' : undefined}
          >
            {products.map((product, index) => (
              <DealSlide
                key={`${audience || 'preview'}-${productKey(product) || index}`}
                product={product}
                position={index}
                isActive={index === activeIndex}
                priority={Math.abs(index - activeIndex) <= 1}
                showHint={!gateOpen && hintVisible && index === 0}
                onEngage={handleSlideEngage}
              />
            ))}
            {loadingMore && (
              <div className="deal-scroller-slide" aria-hidden="true">
                <div className="deal-scroller-loading">LOADING MORE…</div>
              </div>
            )}
          </div>
        )}

        {gateOpen && (
          <AudienceGate
            currentUser={currentUser}
            onSelectAudience={handleSelectAudience}
            onLogin={handleGoogleLoginResult}
          />
        )}
      </div>
    </div>
  );
}

export default DealScroller;
